import { Add } from '@mui/icons-material';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, Divider, IconButton, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultFastTextField } from 'src/components/mui-overrides/Textfield';
import { LaunchpadCard } from 'src/components/ui/LaunchpadCard';
import { POST_WITH_ID } from '@brickwise/asset-validation/dist/asset/assetData';
import { RealestateSizeTextField } from '../atomic-components/RealestateSizeTextField';

interface Props {
  disabled: boolean;
}

const emptyPlot = { kg: '', ez: '', gst: '', widmung: { art: '', bauklasse: '', verbaubarkeit: '', form: '' } };

export const PlotOverview: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const formik = useFormikContext<POST_WITH_ID>();

  const plots = (formik.values as any).propertyDetails?.plots || [];

  const addPlot = () => {
    formik.setFieldValue('propertyDetails.plots', [...plots, { ...emptyPlot, widmung: { ...emptyPlot.widmung } }]);
  };

  const removePlot = (index: number) => {
    const updated = [...plots];
    updated.splice(index, 1);
    formik.setFieldValue('propertyDetails.plots', updated);
  };

  return (
    <LaunchpadCard title={t('dashboard.assets.addedit.main.plotoverview.title')}>
      <RealestateSizeTextField disabled={disabled} />

      <Typography gutterBottom variant="h6">
        {t('dashboard.assets.addedit.main.plotoverview.plots.title')}
      </Typography>

      {plots.map((_: any, index: number) => (
        <div key={index}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {t('dashboard.assets.addedit.main.plotoverview.plots.item', { number: index + 1 })}
            </Typography>
            <IconButton disabled={disabled} onClick={() => removePlot(index)} size="small">
              <RemoveIcon />
            </IconButton>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              columnGap: '16px',
            }}
          >
            <DefaultFastTextField
              disabled={disabled}
              label={t('dashboard.assets.addedit.main.plotoverview.plots.fields.kg.label')}
              fieldName={`propertyDetails.plots.${index}.kg`}
            />
            <DefaultFastTextField
              disabled={disabled}
              label={t('dashboard.assets.addedit.main.plotoverview.plots.fields.ez.label')}
              fieldName={`propertyDetails.plots.${index}.ez`}
            />
            <DefaultFastTextField
              disabled={disabled}
              label={t('dashboard.assets.addedit.main.plotoverview.plots.fields.gst.label')}
              fieldName={`propertyDetails.plots.${index}.gst`}
            />
          </div>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {t('dashboard.assets.addedit.main.plotoverview.plots.widmung.title')}
          </Typography>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: '16px',
            }}
          >
            <DefaultFastTextField
              disabled={disabled}
              label={t('dashboard.assets.addedit.main.plotoverview.plots.widmung.fields.art.label')}
              fieldName={`propertyDetails.plots.${index}.widmung.art`}
            />
            <DefaultFastTextField
              disabled={disabled}
              label={t('dashboard.assets.addedit.main.plotoverview.plots.widmung.fields.bauklasse.label')}
              fieldName={`propertyDetails.plots.${index}.widmung.bauklasse`}
            />
            <DefaultFastTextField
              disabled={disabled}
              label={t('dashboard.assets.addedit.main.plotoverview.plots.widmung.fields.verbaubarkeit.label')}
              fieldName={`propertyDetails.plots.${index}.widmung.verbaubarkeit`}
            />
            <DefaultFastTextField
              disabled={disabled}
              label={t('dashboard.assets.addedit.main.plotoverview.plots.widmung.fields.form.label')}
              fieldName={`propertyDetails.plots.${index}.widmung.form`}
            />
          </div>
          {index < plots.length - 1 && <Divider sx={{ my: 2 }} />}
        </div>
      ))}

      <Button disabled={disabled} variant="contained" startIcon={<Add />} onClick={addPlot} sx={{ mt: 2 }}>
        {t('dashboard.assets.addedit.main.plotoverview.plots.add')}
      </Button>
    </LaunchpadCard>
  );
};
